<script>
  import { createEventDispatcher } from "svelte";
  import { clickOutside } from "$utils/clickOutside";
  import { Icon, ChevronDown, XMark } from "svelte-hero-icons";
  import { cx } from "$lib/utils/cx";
  import { tooltip } from "$lib/utils/tooltip";
  import { persisted } from "$lib/utils/persisted-stores";

  export let value;
  export let label;
  export let options = [];
  export let optional = false;
  export let variant = "default";
  export let showTooltip = false;
  export let isReadonly = false;
  export let shouldPersist = null;
  export let persistedName = null;

  // Allows you to set the value that it clears to
  export let defaultValue = null;

  const dispatch = createEventDispatcher();
  let isOpen = false;

  let persistedStore = null;
  $: if (shouldPersist && $shouldPersist && persistedName) {
    persistedStore = persisted(persistedName, defaultValue);
  }

  $: isObjectOptions = options.length > 0 && options[0] instanceof Object;
  $: normalizedOptions = isObjectOptions
    ? options
    : options.map((opt) => ({ value: opt, label: opt }));
  $: selectedOption = normalizedOptions.find((option) => option.value === value);

  $: {
    if (persistedStore && normalizedOptions) {
      const has_value = normalizedOptions.find((option) => option.value === $persistedStore);
      if (has_value) {
        value = $persistedStore;
      }
    }
  }

  function handleSelect(option) {
    if (!isReadonly) {
      value = option.value;
      if (persistedStore) {
        persistedStore.set(value);
      }
      isOpen = false;
      dispatch("change", value);
      dispatch("input", value);
    }
  }

  function clearValue() {
    if (!isReadonly) {
      value = defaultValue;
      if (persistedStore) {
        persistedStore.set(defaultValue);
      }
      dispatch("change", value);
      dispatch("input", value);
    }
  }

  function toggleDropdown() {
    isOpen = !isOpen;
  }

  function closeDropdown() {
    isOpen = false;
  }
</script>

<div class="flex flex-col basis-full {isReadonly ? 'opacity-60' : ''}">
  <div
    aria-label="Select"
    class="w-full flex items-center {$$props.class || ''}"
    use:clickOutside
    on:clickoutside={closeDropdown}
  >
    <div class="relative w-full">
      <button
        type="button"
        class="flex gap-2 items-center justify-between w-full h-8 text-left rounded p-2 pr-8 text-xs bg-surface-muted border-0 focus:outline-none truncate"
        class:cursor-not-allowed={isReadonly}
        class:focus:ring-2={!isReadonly}
        class:focus:ring-blue-500={!isReadonly}
        class:min-w-8={variant === "narrow"}
        class:min-w-12={variant === "default"}
        class:min-w-16={variant === "medium"}
        class:min-w-32={variant === "wide"}
        class:min-w-40={variant === "ultrawide"}
        on:click={toggleDropdown}
        disabled={isReadonly}
      >
        <span
          class="text-xs text-content-subtle whitespace-nowrap pointer-events-none transition-all truncate shrink-0"
          class:cursor-not-allowed={isReadonly}
        >
          {label}
        </span>

        {#if selectedOption}
          {#if selectedOption.image}
            <img
              src={selectedOption.image}
              alt={selectedOption.label}
              class="w-4 h-4 object-contain mr-2"
            />
          {/if}
          <span
            class="truncate"
            use:tooltip={{
              placement: "top",
              content: showTooltip ? selectedOption.label : null,
              allowHTML: false,
            }}
          >
            {selectedOption.label}
          </span>
        {/if}
      </button>

      <Icon
        src={ChevronDown}
        class={cx(
          "absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle pointer-events-none",
          isReadonly && "opacity-50",
        )}
      />
    </div>

    {#if optional && !isReadonly}
      <button
        type="button"
        class="ml-2 text-content-subtle hover:text-content focus:outline-none"
        on:click={clearValue}
      >
        <Icon src={XMark} class="w-4 h-4" />
      </button>
    {/if}

    <slot name="dynamic" />
  </div>

  <div
    class="z-50 mt-1 py-1 bg-surface border border-line-strong rounded-md shadow-lg max-h-40 overflow-auto top-full"
    class:hidden={!isOpen || isReadonly}
    class:mr-6={optional}
  >
    {#each normalizedOptions as option (option.value)}
      <button
        type="button"
        class="flex items-center w-full px-2 py-1 text-left text-xs hover:bg-surface-hover focus:outline-none focus:bg-surface-muted"
        on:click={() => handleSelect(option)}
      >
        {#if option.image}
          <img src={option.image} alt={option.label} class="w-4 h-4 object-contain mr-2" />
        {/if}
        <span class="truncate">{option.label}</span>
      </button>
    {/each}
  </div>
</div>
